import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { RootState, AppDispatch } from './store';
import { loadSchemaData } from './schemaThunks';
import { ProcessedSchema } from '../types/SchemaTypes';

/**
 * Hook to access schema state from Redux
 */
export const useSchemaState = () => {
    return useSelector((state: RootState) => state.schema);
};

/**
 * Hook to check if schema data is loaded
 */
export const useSchemaLoaded = () => {
    return useSelector((state: RootState) => state.schema.isLoaded);
};

/**
 * Hook to get schema loading state
 */
export const useSchemaLoading = () => {
    return useSelector((state: RootState) => state.schema.loading);
};

/**
 * Hook to get schema error
 */
export const useSchemaError = () => {
    return useSelector((state: RootState) => state.schema.error);
};

/**
 * Hook to get all processed schemas
 */
export const useSchemas = (): ProcessedSchema[] => {
    return useSelector((state: RootState) => state.schema.processedSchemas);
};

/**
 * Hook to get field options
 */
export const useFieldOptions = () => {
    return useSelector((state: RootState) => state.schema.fieldOptions);
};

/**
 * Hook to get field options for a specific entity type
 */
export const useFieldOptionsForEntityType = (entityType: string) => {
    const schemas = useSchemas();

    return useMemo(() => {
        if (!entityType || typeof entityType !== 'string') {
            return [];
        }

        // Find the schema for this entity type (case-insensitive)
        const schema = schemas.find((s) => {
            const schemaId = s.id?.toLowerCase() || '';
            const searchType = entityType.toLowerCase();
            return schemaId === searchType ||
                schemaId === `urn:dcps:scim:v1:${searchType}` ||
                schemaId === `urn:ietf:params:scim:schemas:core:2.0:${searchType}` ||
                schemaId.includes(searchType);
        });

        if (!schema || !schema.attributes) {
            return [];
        }

        return schema.attributes.map((attr) => {
            const attrName = attr.name || '';
            const attrId = attr.id || attrName;

            return {
                name: attrName,
                id: attrId,
                value: attrId,
                label: attrName,
                type: attr.type || 'string',
                required: attr.required || false,
                multiValued: attr.multiValued || false,
                isReference: attr.isReference || false,
                computed: attr.computed || false
            };
        });
    }, [schemas, entityType]);
};

/**
 * Hook to load schema data (with automatic loading if not already loaded)
 */
export const useLoadSchemaData = (autoLoad: boolean = true) => {
    const dispatch = useDispatch<AppDispatch>();
    const isLoaded = useSchemaLoaded();
    const loading = useSchemaLoading();

    const load = useCallback(() => {
        if (!loading && !isLoaded) {
            dispatch(loadSchemaData());
        }
    }, [dispatch, loading, isLoaded]);

    useEffect(() => {
        if (autoLoad && !isLoaded && !loading) {
            load();
        }
    }, [autoLoad, isLoaded, loading, load]);

    return { load, loading, isLoaded };
};

